import React, { useState } from "react";
import {
  Box,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { AudienceType, useLSSearch } from "./LSSearch";
import { useLanguage } from "@tlc/ls2pac/components/Language";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";

interface AudienceListItemButtonProps {
  value: AudienceType;
  label: string;
  onClose: () => void;
}
const AudienceListItemButton = ({
  value,
  label,
  onClose,
}: AudienceListItemButtonProps) => {
  const {
    searchParams: { audience },
    setAudienceFilter,
    clearAudienceFilter,
  } = useLSSearch();

  const handleClickAudience = (type: AudienceType) => {
    if (!type) {
      clearAudienceFilter();
    } else {
      setAudienceFilter(type);
    }
    onClose();
  };

  const isChecked = value === audience;

  return (

    <ListItemButton
      dense={true}
      onClick={() => handleClickAudience(value)}
      role="radio"
      aria-checked={isChecked} 
    >
      <ListItemIcon sx={{ minWidth: 40 }}>
        {isChecked ? (
          <RadioButtonCheckedIcon color="primary" />
        ) : (
          <RadioButtonUncheckedIcon color="action" />
        )}
      </ListItemIcon>
      <ListItemText primary={label} />
    </ListItemButton>
  );
};

export const AudienceFilter = () => {
  const {
    searchParams: { audience },
  } = useLSSearch();
  const { translate } = useLanguage();
  const [open, setOpen] = useState<boolean>(false);

  const getLabel = (audience: AudienceType) => {
    if (audience === AudienceType.AdultGeneral) {
      return translate("audienceAdult");
    }

    if (audience === AudienceType.Juvenile) {
      return translate("audienceJuvenile");
    }

    return translate("audienceAll");
  };

  const handleClick = async () => {
    setOpen(!open);
  };

  const count = Object.values(AudienceType).length;

  return (
    <ListItem sx={{ px: 0, flexDirection: "column", alignItems: "stretch" }}>
      <ListItemButton
        sx={{ gap: 2 }}
        onClick={handleClick}
        divider={!open}
        aria-expanded={open}
        aria-controls="audience-filter-content"
      >
        <ListItemText
          id="audience-filter-header"
          sx={{ my: 0 }}
          primary={
            <>
              {translate("audience")}
              {/* enables screen readers to announce the options count */}
              <Box component="span" sx={visuallyHidden}>
                &nbsp;radio group with {count} items
              </Box>
            </>
          }
          secondaryTypographyProps={{ color: "secondary" }}
          secondary={getLabel(audience)}
        />
        {open ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>
      <Collapse
        in={open}
        timeout="auto"
        unmountOnExit
        id="audience-filter-content"
      >
        <List
          component="div"
          role="radiogroup"
          aria-labelledby="audience-filter-header"
          disablePadding
          dense={true}
        >
          {Object.values(AudienceType).map((value) => {
            const label = getLabel(value);
            return (
              <AudienceListItemButton
                key={label}
                label={label}
                value={value}
                onClose={() => setOpen(false)}
              />
            );
          })}
        </List>
      </Collapse>
    </ListItem>
  );
};
